<template>
  <div
    v-if="currentDevice && layout"
    class="relative transition-opacity duration-200 motion-reduce:transition-none"
    :style="{ opacity: frameReady ? 1 : 0, visibility: frameReady ? 'visible' : 'hidden' }"
  >
    <KeyboardFrame :assets-dir="assetsDir" @ready="frameReady = $event" />

    <div
      class="absolute left-0 top-0 w-full h-full flex items-center justify-center"
      :style="{
        paddingTop: layout?.top + 'px',
        paddingLeft: layout?.left + 'px',
        paddingBottom: layout?.bottom + 'px',
        paddingRight: layout?.right + 'px',
      }"
    >
      <Keyboard
        class="shrink-0"
        :keyboard-config="keyboardConfig!"
        :gap="5"
        :key-tooltip="{
          enabled: (key) =>
            (isModified(key) || isSpecialIconKey(key)) && !getTargetKeyDisabled(key),
          open: (key) => activeTooltipKeyCode === key.keywords && !getTargetKeyDisabled(key),
          side: 'top',
          sideOffset: 4,
          class: 'w-fit max-h-13',
          onOpenChange: handleTooltipOpen,
        }"
        :key-class="getTargetKeyClass"
        :native-event-names="targetKeyboardEventNames"
        @key-event="handleKeyEvent"
      >
        <template #widget>
          <component :is="widgetMap[model]" v-if="model" />
        </template>
        <!-- 改键状态按键文本自定义 -->
        <template #key-label="{ config }">
          <template v-if="isModified(config)">
            <div class="relative w-full h-full flex items-center justify-center select-none">
              <div class="w-full h-full flex items-center justify-center gap-1 overflow-hidden">
                <!-- 按键内容为 icon + [iconText] -->
                <template v-if="getKeySetting(config)?.raw?.icon">
                  <div
                    class="flex items-center justify-center relative w-7 h-7"
                    :class="
                      getKeySetting(config)?.keyType === 'configuration' &&
                      'hover:text-neutral-dark-active'
                    "
                  >
                    <Icon
                      :icon="getKeySetting(config)?.raw?.icon"
                      :class="`shrink-0 relative ${config.keywords !== 'RT' ? 'w-5 h-5 top-1' : 'w-full h-full'}`"
                    />
                  </div>
                  <span
                    v-if="config.keywords !== 'RT'"
                    class="absolute right-0.5 -top-0.5 text-[10px]"
                  >
                    {{ getKeyOriginalLabel(config) }}
                  </span>
                </template>
                <!-- 按键内容类别为 MT/TGL/DKS -->
                <template
                  v-else-if="
                    getKeySetting(config)?.keyType === 'MT' ||
                    getKeySetting(config)?.keyType === 'TGL' ||
                    getKeySetting(config)?.keyType === 'DKS'
                  "
                >
                  <span class="whitespace-pre-wrap leading-4">
                    {{ getKeyOriginalLabel(config) }}
                  </span>
                  <span
                    :class="[
                      'absolute right-0 -bottom-1.25 text-[10px]',
                      `${getKeySetting(config)?.keyType === 'MT' ? 'text-brand-orange' : getKeySetting(config)?.keyType === 'TGL' ? 'text-green-active' : 'text-brand-blue1'}`,
                    ]"
                  >
                    {{ getKeySetting(config)?.keyType }}
                  </span>
                </template>
                <!-- 按键内容为 文本 -->
                <template v-else>
                  <!-- ① bottom 模式：有角标 + 需要换行 -->
                  <div v-if="isBottomMode(config)" class="flex flex-col w-full h-full">
                    <span class="flex items-center justify-end h-3.25 w-full text-[10px] text-end">
                      {{ getKeyOriginalLabel(config) }}
                    </span>
                    <span
                      class="flex flex-1 w-full pl-0.75 items-end"
                      :class="[
                        getKeyLabelLines(config).length > 1 ? 'text-xs-semibold' : 'text-[12px]',
                        getHoverTextClass(config),
                      ]"
                    >
                      <span class="leading-3">
                        <template v-for="(line, i) in getKeyLabelLines(config)" :key="i">
                          {{ line }}<br v-if="i < getKeyLabelLines(config).length - 1" />
                        </template>
                      </span>
                    </span>
                  </div>

                  <!-- ② center 模式：无角标，或有角标但文本短且无换行符 -->
                  <div v-else class="relative w-full h-full">
                    <span
                      class="absolute top-0 right-0 h-3.25 flex items-center justify-end w-full text-[10px] text-end px-0.5"
                    >
                      {{ getKeyOriginalLabel(config) }}
                    </span>
                    <span
                      class="absolute inset-0 flex items-center justify-center text-[12px]"
                      :class="getHoverTextClass(config)"
                    >
                      {{ getKeyLabel(config) }}
                    </span>
                  </div>
                </template>
              </div>
            </div>
          </template>
          <!-- 初始图标按键 -->
          <template v-else-if="ICON_KEYWORDS.includes(config.keywords)">
            <div class="flex items-center justify-center w-7 h-7">
              <Icon
                :icon="ICON_KEYWORDS_MAP[config.keywords]"
                class="w-7 h-7 text-black-white group-hover:text-text-hover"
              />
            </div>
          </template>
          <!-- V700diy-87 三个灯效功能键：未改键（出厂）状态以图标展示。
               图标颜色继承按键（getTargetKeyClass），保证默认/hover/选中在明暗主题下均可见。 -->
          <template v-else-if="isSpecialIconKey(config)">
            <div class="flex items-center justify-center w-7 h-7">
              <Icon :icon="V700DIY87_SPECIAL_ICON_MAP[config.keywords]" class="w-7 h-7" />
            </div>
          </template>
        </template>

        <!-- 按键 tooltip -->
        <template #key-tooltip="{ config }">
          <div
            class="min-w-17 w-full p-0.5 flex flex-col items-center bg-white rounded-lg"
            :class="
              showRestoreKey(config) || getKeySetting(config)?.isFactoryDefault ? 'h-13' : 'h-7'
            "
          >
            <div
              class="w-full h-6 px-2 flex justify-center items-center text-s-medium text-text-disabled cursor-not-allowed"
            >
              {{
                isValueOnlyTooltip(config)
                  ? getValueOnlyTooltipValue(config)
                  : getTooltipLabel(config)
              }}
            </div>
            <!-- 出厂默认：第二行只读提示 -->
            <div
              v-if="getKeySetting(config)?.isFactoryDefault"
              class="w-full h-6 flex justify-center items-center text-s-medium text-text-disabled cursor-not-allowed"
            >
              {{ $t('keys.targetPanel.currentFactoryDefault') }}
            </div>
            <!-- 已改键：恢复按钮 -->
            <div
              v-else-if="showRestoreKey(config)"
              class="w-full h-6 flex justify-center items-center transition-colors text-s-medium text-neutral-dark-default rounded-6xl cursor-pointer hover:bg-neutral-dark-hover hover:text-white active:bg-neutral-dark-active"
              @click="restoreKey(config)"
            >
              {{ getTooltipSecondaryLabel(config) }}
            </div>
          </div>
        </template>
      </Keyboard>
    </div>
  </div>
</template>

<script setup lang="ts">
  import { HTMLAttributes, inject, nextTick, onUnmounted, watch, computed, ref } from 'vue'
  import { storeToRefs } from 'pinia'
  import { Icon, Keyboard, toast } from '@rapoo/element'
  import { useDeviceStore, useLocaleStore } from '@/stores'
  import { getDragData, DragType } from '@/utils/dnd'
  import widgetMap from '@/components/keyboardWidget'
  import KeyboardFrame from '@/components/KeyboardFrame.vue'
  import { useGlobalContextmenu } from '@/composables/useGlobalContextmenu'
  import { useKeyTooltip } from '../composables/useKeyTooltip'
  import { useMyKeysConf } from '../composables/useMyKeysConf'
  import {
    KEYCODE_KEYWORDS_MAP,
    V700DIY87_SPECIAL_ICON_MAP,
    buildFactoryDefaultSetting,
    getLocalizedField,
    isRemapRestrictedKey,
    isV700Diy87SpecialKey,
  } from '../utils/keyMapSettings'
  import { getFactoryKeyLabel, pickFactoryKeyMap } from '../utils/factoryKeys'
  import { SPECIAL_KEY_META_BY_KEYWORDS } from '../utils/specialKeyMeta'
  import { findSettingIndexByTargetKey, isSameKeyIdentity, parseNumber } from '../utils/keyIdentity'
  import {
    SelectedKeySymbol,
    KeySettingsSymbol,
    RightSelectedKeySymbol,
    ActiveTabValueSymbol,
    SuppressSelectedKeyUxSymbol,
  } from '../types'

  import type {
    KeyConfig,
    KeyEventName,
    KeyboardConfig,
    KeyboardKeyEventPayload,
  } from '@rapoo/element'
  import type { KeyPlatform, KeySetting, KeyChangePayload, KeysCapability } from '../types'

  const { t } = useLocaleStore()

  // 初始按键内容为图标的键
  const ICON_KEYWORDS = ['RT']

  // 图标按键标签映射
  const ICON_KEYWORDS_MAP: Record<string, string> = {
    RT: 'Hardware-Fill-rt',
  }

  // V700diy-87 三个灯效功能键在 tooltip 中展示的功能描述（i18n key）。
  // 图标映射与型号判断复用 keyMapSettings 中的共享实现（灯光页/改键页一致）。
  const SPECIAL_ICON_KEY_DESC: Record<string, string> = {
    LEDMode_Forward: 'keys.targetPanel.v700BacklightMode',
    SideLED_Mode: 'keys.targetPanel.v700SideLightMode',
    LED_circulate: 'keys.targetPanel.v700BacklightBrightness',
  }

  const targetKeyboardEventNames: KeyEventName[] = [
    'click',
    'contextmenu',
    'mouseenter',
    'dragenter',
    'dragover',
    'dragleave',
    'drop',
  ]

  // 依赖注入
  const selectedKey = inject(SelectedKeySymbol)
  const rightSelectedKey = inject(RightSelectedKeySymbol)
  const keySettings = inject(KeySettingsSymbol)
  const activeTabValue = inject(ActiveTabValueSymbol)
  const suppressSelectedKeyUx = inject(SuppressSelectedKeyUxSymbol, ref(false))
  const shouldListenGlobalContextmenu = computed(
    () => Boolean(selectedKey?.value) && !suppressSelectedKeyUx.value,
  )

  const props = defineProps<{
    isFn: boolean
    isReadOnly: boolean
    platform: KeyPlatform
  }>()
  const emits = defineEmits<{
    'update-setting': [data: any, targetKeyCode: KeyConfig]
  }>()

  const { restoreMappedKey } = useMyKeysConf()

  const deviceStore = useDeviceStore()
  const { currentDevice } = storeToRefs(deviceStore)
  const layout = computed(() => currentDevice.value?.manifest.layout)
  const model = computed(() => currentDevice.value?.manifest.model)

  const factoryKeys = computed(() => {
    const cap = currentDevice.value?.manifest.capabilities?.find(
      (c): c is KeysCapability => c.type === 'keys',
    )
    return pickFactoryKeyMap(cap?.config.factoryKeys, props.platform)
  })

  /** 出厂定义仅在 Fn 层生效，默认层出厂即物理键自身；表按当前 Win/Mac 层选取 */
  const getFactoryKeywords = (key?: KeyConfig | string): string | undefined => {
    if (!props.isFn) return undefined
    const keywords = typeof key === 'string' ? key : key?.keywords
    if (!keywords) return undefined
    return factoryKeys.value[keywords]
  }
  const assetsDir = computed(() => currentDevice.value?.manifest?.ui?.assetsDir || '')
  const frameReady = ref(false)

  const keyboardConfig = computed<KeyboardConfig | null>(() => {
    if (!layout.value) return null

    return {
      ...layout.value,
      keys: layout.value.keys.map((row) =>
        row.map((key) => {
          const { w, h, x, y, keywords, ...rest } = key
          const config: KeyConfig = KEYCODE_KEYWORDS_MAP.get(keywords)!

          return {
            ...rest,
            ...config,
            disabled: props.isReadOnly || isRemapRestrictedKey(keywords),
            rect: {
              w,
              h,
              x,
              y,
            },
          }
        }),
      ),
    }
  })

  /**
   * 按 keywords 获取目标键的 setting。
   *
   * @param key 目标键或 keyCode
   * @returns 命中的 setting；未命中返回 undefined
   */
  const getKeySetting = (key?: KeyConfig | string): KeySetting | undefined => {
    let targetKey
    if (typeof key === 'string') {
      targetKey = key
    } else {
      targetKey = key?.keywords
    }
    if (!targetKey) return undefined
    return keySettings?.value.find((item) => item.keywords === targetKey)
  }

  /**
   * 判断目标键是否被改键。
   *
   * @param key 键对象或 keyCode
   * @returns 是否被改键
   */
  const isModified = (key?: KeyConfig | string): boolean => {
    return Boolean(getKeySetting(key)?.isModified)
  }

  /**
   * 是否为 V700diy-87 的特殊灯效键（背光/侧灯/装饰灯切换）。
   * 仅该型号命中，避免影响其它键盘的通用改键逻辑。
   *
   * @param key 键对象或 keyCode
   * @returns 是否为该型号的特殊灯效键
   */
  const isSpecialIconKey = (key?: KeyConfig | string): boolean => {
    const keywords = typeof key === 'string' ? key : key?.keywords
    return isV700Diy87SpecialKey(model.value, keywords)
  }

  /**
   * 是否在 tooltip 中展示恢复按钮。
   * - 出厂键未改（isFactoryDefault）：不展示
   * - 出厂键被改走：展示「恢复出厂默认」
   * - 普通已改键：展示「恢复按键」
   */
  const showRestoreKey = (key: KeyConfig): boolean => {
    if (props.isReadOnly) return false
    if (isValueOnlyTooltip(key)) return false
    const setting = getKeySetting(key)
    if (!setting?.isModified) return false
    // 出厂默认态不可恢复
    if (setting.isFactoryDefault) return false
    if (isSpecialIconKey(key)) return true
    return true
  }

  /** 是否为「有出厂定义且已被改走」的键 */
  const isRemappedFactoryKey = (key: KeyConfig) => {
    const setting = getKeySetting(key)
    return Boolean(
      getFactoryKeywords(key) && setting?.isModified && setting.isFactoryDefault === false,
    )
  }

  const {
    activeTooltipKeyCode,
    clearTooltipTimer,
    openTooltipWithAuto,
    openTooltipWithHover,
    handleTooltipOpen,
    clearTooltip,
  } = useKeyTooltip((keyCode) => isModified(keyCode) || isSpecialIconKey(keyCode))

  // 更新keySettings
  const updateKeySettings = (newKeySettings: KeySetting[]) => {
    keySettings!.value = newKeySettings
  }

  watch(
    () => rightSelectedKey!.value,
    async (keywords) => {
      if (!keywords) return
      // 禁用无法操作的按键
      if (getTargetKeyDisabled(keywords)) {
        rightSelectedKey!.value = undefined
        return
      }
      if (isValueOnlyTooltipKeyType(getKeySetting(keywords)?.keyType)) {
        rightSelectedKey!.value = undefined
        return
      }
      await nextTick()
      openTooltipWithAuto(keywords)
      rightSelectedKey!.value = undefined
    },
  )

  onUnmounted(() => {
    clearTooltipTimer()
  })

  // 恢复按键操作
  const restoreKey = async (key: KeyConfig) => {
    if (getTargetKeyDisabled(key)) {
      return
    }

    const keySetting = getKeySetting(key)
    const factoryKeywords = getFactoryKeywords(key)

    const payload = await restoreMappedKey({
      setting: keySetting,
      defaultKey: key,
      factoryKeywords,
      isFn: props.isFn,
      platform: props.platform,
    })

    const updatedSettings = [...keySettings!.value]
    const targetIndex = findSettingIndexByTargetKey(updatedSettings, key)
    if (targetIndex >= 0) {
      if (factoryKeywords) {
        const factorySetting = buildFactoryDefaultSetting({
          physicalKeywords: key.keywords,
          factoryKeywords,
          index: payload?.index ?? parseNumber(key.key) ?? 0,
        })
        if (factorySetting) {
          updatedSettings[targetIndex] = factorySetting
        } else {
          updatedSettings.splice(targetIndex, 1)
        }
      } else {
        updatedSettings.splice(targetIndex, 1)
      }
    }
    updateKeySettings(updatedSettings)
    clearTooltip(key.keywords)
  }

  const dragHoverKeyCode = ref<string>('')

  // 当前选中按键的提示 id：宏 tab 与其他 tab 使用不同 id 以便互相切换/独立 dismiss
  const KEYBOARD_TOAST_ID = 'keyboard'
  const MACRO_TOAST_ID = 'macro-bind-select-macro'

  const isOnMacroTab = computed(() => activeTabValue?.value === 'macro')

  /**
   * 在虚拟键盘上选中按键后，根据当前所在 tab 展示对应的提示。
   * 宏 tab：展示宏专属提示（macroBindSelectMacro）
   * 其他 tab：展示通用提示（deselectKey）
   */
  const showSelectionToast = () => {
    if (suppressSelectedKeyUx.value) return
    if (isOnMacroTab.value) {
      toast.dismiss(KEYBOARD_TOAST_ID)
      toast.info(t('toast.keys.macroBindSelectMacro'), {
        id: MACRO_TOAST_ID,
        duration: Infinity,
      })
    } else {
      toast.dismiss(MACRO_TOAST_ID)
      toast(t('toast.deselectKey'), {
        id: KEYBOARD_TOAST_ID,
        duration: Infinity,
        class: 'text-state-success',
      })
    }
  }

  const cancelSelectedKeyByGlobalContextmenu = () => {
    if (!selectedKey?.value) return

    selectedKey.value = null
    dragHoverKeyCode.value = ''
    toast.dismiss(KEYBOARD_TOAST_ID)
    toast.dismiss(MACRO_TOAST_ID)
  }

  // 全局监听右键取消选中
  useGlobalContextmenu({
    enabled: shouldListenGlobalContextmenu,
    onContextmenu: cancelSelectedKeyByGlobalContextmenu,
  })

  /**
   * 判断目标键是否应禁用交互。
   * Fn / RT 物理键禁止改键；出厂功能键允许修改。
   */
  const getTargetKeyDisabled = (targetKey: KeyConfig | string): boolean => {
    const targetKeywords = typeof targetKey === 'string' ? targetKey : targetKey.keywords

    if (props.isReadOnly) return true

    // Fn / RT 禁用
    if (isRemapRestrictedKey(targetKeywords)) return true

    return false
  }

  // 处理按键事件
  const handleKeyEvent = (ctx: KeyboardKeyEventPayload) => {
    const { data, eventName, nativeEvent } = ctx

    if (!selectedKey) return

    // 按键点击
    if (eventName === 'click') {
      if (getTargetKeyDisabled(data)) {
        if (props.isReadOnly) {
          toast.info(t('toast.keys.keyNotSupported'))
          return
        }
        toast.info(t('toast.keys.fnNotSupported'))
        return
      }

      showSelectionToast()

      // RS/SOCD 选键：每次点击都写入，便于同一键清空后再次填充
      if (suppressSelectedKeyUx.value) {
        selectedKey.value = { ...data }
        return
      }

      // 点击的按键不是选中的按键
      const isSameTargetKey = isSameKeyIdentity(selectedKey.value, data)
      if (!isSameTargetKey) {
        selectedKey.value = data
      }
    }

    // 拖拽进入按键
    if (eventName === 'dragover') {
      const event = nativeEvent as DragEvent

      dragHoverKeyCode.value = data.keywords

      if (getTargetKeyDisabled(data)) {
        return false
      }

      event.preventDefault()
      event.dataTransfer!.dropEffect = 'copy'

      return
    }

    // 拖拽移出按键
    if (eventName === 'dragleave') {
      const event = nativeEvent as DragEvent

      if (dragHoverKeyCode.value === data.keywords) {
        dragHoverKeyCode.value = ''
      }

      event.preventDefault()

      return
    }

    // 拖拽放下
    if (eventName === 'drop') {
      const event = nativeEvent as DragEvent
      const dataTransfer = event.dataTransfer
      if (!dataTransfer) return

      if (getTargetKeyDisabled(data)) {
        return
      }

      event.preventDefault()
      event.stopPropagation()

      const draggedData = getDragData<KeyChangePayload>(dataTransfer, DragType.KEY_MAPPING)

      if (draggedData) {
        dragHoverKeyCode.value = ''
        draggedData.keywords = data.keywords
        toast.dismiss(KEYBOARD_TOAST_ID)
        toast.dismiss(MACRO_TOAST_ID)
        openTooltipWithHover(data.keywords)

        emits('update-setting', draggedData, data)
      }

      return
    }

    // 鼠标移入按键，处理 hover tooltip（特殊灯效键即使未改键也需展示功能描述）
    if (eventName === 'mouseenter') {
      if (getTargetKeyDisabled(data)) return
      if (!isModified(data) && !isSpecialIconKey(data)) return
      openTooltipWithHover(data.keywords)
      return
    }
  }

  // 获取按键的自定义文本（已改键用 alias，原始按键名用 name）
  const getKeyLabel = (key?: KeyConfig): string => {
    const setting = getKeySetting(key)
    if (setting) {
      if (setting.keyType === 'RS' || setting.keyType === 'SOCD') {
        return setting.keyType
      }

      if (setting.keyType === 'macro') {
        return 'Macro'
      }

      if (setting.target?.keywords === 'Disable key') {
        return ''
      }

      // 机型配置的自定义描述优先（keycode 表覆盖不到的出厂功能、需要改写文案的键）
      if (setting.displayLabel) return String(setting.displayLabel)

      // 多媒体 / 灯效键优先用 name（含换行文案），常规键用 alias
      const target = setting.target as KeyConfig | undefined
      const preferName =
        setting.keyType === 'media' || SPECIAL_KEY_META_BY_KEYWORDS.has(target?.keywords || '')
      const field = preferName ? target?.name : target?.alias
      return getLocalizedField(field)
    }

    return ''
  }

  function getKeyLabelLines(key: KeyConfig): string[] {
    const label = getKeyLabel(key) ?? ''
    return label.replace(/_/g, '\n').split('\n')
  }

  function isBottomMode(key: KeyConfig) {
    const label = getKeyLabel(key) ?? ''
    const cornerLabel = getKeyOriginalLabel(key)
    if (!cornerLabel) return false // 无角标 → center
    return (
      label.includes('\n') || // 显式换行
      label.includes('_') || // 下划线换行
      label.length > 7 // 超长文本
    )
  }

  const getKeyOriginalLabel = (key?: KeyConfig) => {
    // 特殊灯效键右上角不展示原按键（其“原按键”仅在 tooltip 中体现）
    if (isSpecialIconKey(key)) return ''

    const config = KEYCODE_KEYWORDS_MAP.get(key?.keywords || '')

    if (config) {
      return getLocalizedField(config.alias)
    }

    return ''
  }

  // 按键 tooltip label
  const getTooltipLabel = (key: KeyConfig) => {
    const keySetting = getKeySetting(key)
    const keyType = keySetting?.keyType
    const factoryKeywords = getFactoryKeywords(key)

    // 特殊灯效键：第一行仅展示功能描述（不带“原按键:”前缀）
    if (isSpecialIconKey(key)) {
      const descKey = SPECIAL_ICON_KEY_DESC[key.keywords]
      return descKey ? t(descKey) : ''
    }

    // 出厂默认态：原按键: 物理键名
    if (keySetting?.isFactoryDefault) {
      return t('keys.targetPanel.originalKey', {
        label: getLocalizedField(key?.alias),
      })
    }

    // 出厂键被改走：出厂默认: 出厂功能名
    if (factoryKeywords && keySetting && !keySetting.isFactoryDefault) {
      return t('keys.targetPanel.factoryDefaultKey', {
        label: getFactoryKeyLabel(factoryKeywords),
      })
    }

    if (keyType === 'normal' || keyType === 'media' || keyType === 'configuration') {
      return t('keys.targetPanel.originalKey', { label: getLocalizedField(key?.alias) })
    }

    if (keyType === 'macro') {
      return keySetting?.raw?.macroName || t('keys.targetPanel.macroFallback')
    }

    if (keyType === 'MT') {
      return t('keys.targetPanel.mtTooltip', {
        click: keySetting?.raw?.clickKeyLabel,
        press: keySetting?.raw?.pressKeyLabel,
      })
    }

    if (keyType === 'TGL') {
      return t('keys.targetPanel.tglTooltip', {
        click: keySetting?.raw?.clickKeyLabel,
      })
    }

    if (keyType === 'DKS') {
      return t('keys.targetPanel.dksTooltip', {
        keys: keySetting?.raw?.dksKeyLabels,
      })
    }

    return ''
  }

  /** tooltip 第二行文案：出厂默认提示 / 恢复出厂 / 恢复按键 */
  const getTooltipSecondaryLabel = (key: KeyConfig) => {
    if (getKeySetting(key)?.isFactoryDefault) {
      return t('keys.targetPanel.currentFactoryDefault')
    }
    if (isRemappedFactoryKey(key)) {
      return t('keys.targetPanel.restoreFactoryDefault')
    }
    return t('keys.targetPanel.restoreKey')
  }

  // 这类按键（RS，SOCD）tooltip 只展示阈值，不展示“恢复按键”按钮。
  const VALUE_ONLY_TOOLTIP_KEY_TYPES = new Set<KeySetting['keyType']>(['RS', 'SOCD'])
  const RS_SLIDER_MAX = 255
  const RS_MM_MIN = 0.01
  const RS_MM_MAX = 3.4
  const RS_THRESHOLD_MIN = RS_MM_MIN * 100
  const RS_THRESHOLD_MAX = RS_MM_MAX * 100
  const RS_THRESHOLD_RANGE = RS_THRESHOLD_MAX - RS_THRESHOLD_MIN

  const isValueOnlyTooltipKeyType = (keyType?: KeySetting['keyType']) => {
    return Boolean(keyType && VALUE_ONLY_TOOLTIP_KEY_TYPES.has(keyType))
  }

  const isValueOnlyTooltip = (key: KeyConfig) =>
    isValueOnlyTooltipKeyType(getKeySetting(key)?.keyType)

  const getValueOnlyTooltipValue = (key: KeyConfig) => {
    const keySetting = getKeySetting(key)
    if (keySetting?.keyType !== 'RS' && keySetting?.keyType !== 'SOCD') return '-'

    const threshold = parseNumber(
      keySetting?.keyType === 'RS'
        ? keySetting?.raw?.rs?.triggerThreshold
        : keySetting?.raw?.socd?.triggerThreshold,
    )
    if (threshold == null) return '-'

    const sliderValue = Math.min(
      RS_SLIDER_MAX,
      Math.max(
        0,
        Math.round(((threshold - RS_THRESHOLD_MIN) / RS_THRESHOLD_RANGE) * RS_SLIDER_MAX),
      ),
    )
    const mmValue = (RS_MM_MIN + (sliderValue / RS_SLIDER_MAX) * (RS_MM_MAX - RS_MM_MIN)).toFixed(2)

    return `${sliderValue}(≈${mmValue} mm)`
  }

  // RS/SOCD/macro 按键中间文本颜色
  function getHoverTextClass(config: KeyConfig) {
    const keyType = getKeySetting(config)?.keyType
    if (keyType === 'RS' || keyType === 'SOCD' || keyType === 'macro') {
      return 'hover:text-text-important-default'
    }
    return ''
  }

  /**
   * 按键样式
   */
  const getTargetKeyClass = (key: KeyConfig): HTMLAttributes['class'] => {
    const keySetting = getKeySetting(key)

    // Fn / RT：禁用态，不展示已改键高亮
    if (isRemapRestrictedKey(key.keywords)) {
      return 'bg-key transition-all text-black-white cursor-not-allowed pointer-events-none'
    }

    // 是否为选中按键
    const isActive = !suppressSelectedKeyUx.value && isSameKeyIdentity(selectedKey?.value, key)
    // 拖拽时 hover
    const isDragHover = Boolean(dragHoverKeyCode.value) && dragHoverKeyCode.value === key.keywords

    if (keySetting && keySetting.isModified) {
      switch (keySetting.keyType) {
        case 'normal':
          return `bg-fluo-default text-black transition-all hover:bg-fluo-hover hover:text-text-hover ${isActive && 'bg-brand-fluo text-text-active'} ${isDragHover && 'bg-fluo-hover'}`
        case 'media':
          return `bg-fluo-default text-black transition-all hover:bg-fluo-hover hover:text-text-hover ${isActive && 'bg-brand-fluo text-text-active'} ${isDragHover && 'bg-fluo-hover'}`
        case 'configuration':
          return `bg-green-default text-neutral-dark-active transition-all hover:bg-green-hover ${isActive && 'bg-green-active'} ${isDragHover && 'bg-green-hover'}`
        case 'DKS':
          return `bg-key text-text-glass-default transition-all hover:text-text-glass-hover ${isActive && 'border-brand-blue1 text-black'}`
        case 'MT':
          return `bg-key text-text-glass-default transition-all hover:text-text-glass-hover ${isActive && 'border-orange-active text-black'}`
        case 'TGL':
          return `bg-key text-text-glass-default transition-all hover:text-text-glass-hover ${isActive && 'border-green-active text-black'}`
        case 'RS':
          return `bg-purple-default text-text-important-default transition-all hover:bg-purple-hover ${isActive && 'bg-purple-active'}`
        case 'SOCD':
          return `bg-pink-default text-text-important-default transition-all hover:bg-pink-hover ${isActive && 'bg-pink-active'}`
        case 'macro':
          return `bg-cyan-default text-text-important-default transition-all hover:bg-cyan-hover ${isActive && 'bg-cyan-active'}`
        default:
          return `bg-fluo-default text-black transition-all hover:bg-fluo-hover hover:text-text-hover ${isActive && 'bg-brand-fluo text-text-active'} ${isDragHover && 'bg-fluo-hover'}`
      }
    }

    // 按键默认样式
    return `bg-key transition-all text-black-white ${isActive && 'border-brand-blue1 bg-white text-black hover:text-black'}`
  }
</script>

<style scoped></style>
